import { useState } from "react";
import { DesktopLayout } from "../../components/DesktopLayout";
import { useApp } from "../../context/AppContext";
import { categoriasOcorrencia, formatDataHora } from "../../data/mock";
import { PriorityBadge, StatusBadge } from "../../components/StatusBadge";
import {
  AlertTriangle,
  Filter,
  UserCircle,
  MapPin,
  Clock,
  Eye,
  CheckCircle2,
  X,
  FileText,
  ShieldCheck,
  AlertCircle,
  Activity,
} from "lucide-react";

const card = {
  background: "rgba(255,255,255,0.03)",
  border: "1px solid rgba(255,255,255,0.08)",
};

export default function OcorrenciasDesktop() {
  const { ocorrencias, vigilantes, postos, getById } = useApp();

  const [cat, setCat]                       = useState("todas");
  const [prio, setPrio]                     = useState("todas");
  const [ocorrenciaAberta, setOcorrenciaAberta] = useState(null);
  const [resolvidasFake, setResolvidasFake] = useState([]);

  const ocorrenciasComStatus = ocorrencias.map((ocorrencia) => ({
    ...ocorrencia,
    status: resolvidasFake.includes(ocorrencia.id) ? "resolvida" : ocorrencia.status,
  }));

  const filtered = ocorrenciasComStatus.filter(
    (oc) =>
      (cat === "todas" || oc.categoria === cat) &&
      (prio === "todas" || oc.prioridade === prio),
  );

  const abertas    = filtered.filter((oc) => oc.status !== "resolvida");
  const resolvidas = filtered.filter((oc) => oc.status === "resolvida");

  const totalAbertas    = ocorrenciasComStatus.filter((oc) => oc.status !== "resolvida").length;
  const totalResolvidas = ocorrenciasComStatus.filter((oc) => oc.status === "resolvida").length;
  const totalAlta       = ocorrenciasComStatus.filter((oc) => oc.prioridade === "alta").length;

  const marcarComoResolvida = (id) => {
    setResolvidasFake((atuais) => (atuais.includes(id) ? atuais : [...atuais, id]));
    setOcorrenciaAberta((atual) => (atual ? { ...atual, status: "resolvida" } : atual));
  };

  const selectStyle = {
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "rgba(255,255,255,0.7)",
    borderRadius: "10px",
    padding: "8px 12px",
    fontSize: "13px",
    outline: "none",
  };

  return (
    <DesktopLayout
      title="Ocorrências"
      subtitle="Acompanhamento dos registros relevantes do plantão"
    >
      {/* KPIs */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <KpiCard icon={FileText}     label="Total"           value={ocorrenciasComStatus.length} color="blue"   />
        <KpiCard icon={AlertTriangle} label="Abertas"        value={totalAbertas}                color="amber"  />
        <KpiCard icon={AlertCircle}  label="Alta prioridade" value={totalAlta}                  color="red"    />
        <KpiCard icon={CheckCircle2} label="Resolvidas"      value={totalResolvidas}            color="emerald"/>
      </div>

      {/* Filtros */}
      <div className="rounded-2xl p-4 mb-6 flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4" style={card}>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4" style={{ color: "rgba(255,255,255,0.3)" }} />
          <span className="text-[12px] font-semibold tracking-wider uppercase" style={{ color: "rgba(255,255,255,0.3)" }}>Filtros</span>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <select
            data-testid="filter-categoria"
            value={cat}
            onChange={(e) => setCat(e.target.value)}
            style={selectStyle}
          >
            <option value="todas">Todas as categorias</option>
            {categoriasOcorrencia.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>

          <select
            data-testid="filter-prioridade"
            value={prio}
            onChange={(e) => setPrio(e.target.value)}
            style={selectStyle}
          >
            <option value="todas">Todas as prioridades</option>
            <option value="alta">Alta</option>
            <option value="media">Média</option>
            <option value="baixa">Baixa</option>
          </select>
        </div>
      </div>

      {/* Kanban */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        {/* Coluna: Abertas */}
        <div>
          <div className="flex items-center gap-2.5 mb-4">
            <span
              className="w-2 h-2 rounded-full"
              style={{ background: "#F59E0B" }}
            />
            <h3 className="text-[14px] font-bold text-white">Abertas</h3>
            {abertas.length > 0 && (
              <span
                className="text-[11px] font-bold px-2 py-0.5 rounded-md"
                style={{ background: "rgba(245,158,11,0.12)", color: "#F59E0B" }}
              >
                {abertas.length}
              </span>
            )}
          </div>

          <div className="space-y-3">
            {abertas.map((oc) => {
              const vigilante = getById(vigilantes, oc.vigilanteId);
              const posto = getById(postos, oc.postoId);
              return (
                <OcorrenciaCard
                  key={oc.id}
                  oc={oc}
                  vigilante={vigilante}
                  posto={posto}
                  resolvida={false}
                  onAcompanhar={() => setOcorrenciaAberta({ ...oc, vigilante, posto })}
                />
              );
            })}
            {abertas.length === 0 && (
              <div
                className="rounded-2xl p-6 text-center"
                style={{ background: "rgba(16,185,129,0.04)", border: "1px solid rgba(16,185,129,0.1)" }}
              >
                <CheckCircle2 className="w-6 h-6 mx-auto mb-2" style={{ color: "#10B981" }} />
                <p className="text-[13px]" style={{ color: "rgba(255,255,255,0.35)" }}>
                  {prio !== "todas" || cat !== "todas" ? "Nenhuma encontrada com esses filtros." : "Nenhuma ocorrência aberta."}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Coluna: Resolvidas */}
        <div>
          <div className="flex items-center gap-2.5 mb-4">
            <span
              className="w-2 h-2 rounded-full"
              style={{ background: "#10B981" }}
            />
            <h3 className="text-[14px] font-bold text-white">Resolvidas</h3>
            {resolvidas.length > 0 && (
              <span
                className="text-[11px] font-bold px-2 py-0.5 rounded-md"
                style={{ background: "rgba(16,185,129,0.12)", color: "#34d399" }}
              >
                {resolvidas.length}
              </span>
            )}
          </div>

          <div className="space-y-3">
            {resolvidas.map((oc) => {
              const vigilante = getById(vigilantes, oc.vigilanteId);
              const posto = getById(postos, oc.postoId);
              return (
                <OcorrenciaCard
                  key={oc.id}
                  oc={oc}
                  vigilante={vigilante}
                  posto={posto}
                  resolvida
                  onAcompanhar={() => setOcorrenciaAberta({ ...oc, vigilante, posto })}
                />
              );
            })}
            {resolvidas.length === 0 && (
              <div
                className="rounded-2xl p-6 text-center"
                style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}
              >
                <p className="text-[13px]" style={{ color: "rgba(255,255,255,0.25)" }}>Nenhuma resolvida ainda.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {ocorrenciaAberta && (
        <OcorrenciaModal
          ocorrencia={ocorrenciaAberta}
          onClose={() => setOcorrenciaAberta(null)}
          onResolver={() => marcarComoResolvida(ocorrenciaAberta.id)}
        />
      )}
    </DesktopLayout>
  );
}

const KpiCard = ({ icon: Icon, label, value, color }) => {
  const colors = {
    blue:    { bg: "rgba(59,130,246,0.08)",  border: "rgba(59,130,246,0.15)",  text: "#3B82F6" },
    amber:   { bg: "rgba(245,158,11,0.08)",  border: "rgba(245,158,11,0.15)",  text: "#F59E0B" },
    red:     { bg: "rgba(239,68,68,0.08)",   border: "rgba(239,68,68,0.15)",   text: "#EF4444" },
    emerald: { bg: "rgba(16,185,129,0.08)",  border: "rgba(16,185,129,0.15)",  text: "#10B981" },
  };
  const c = colors[color] || colors.blue;

  return (
    <div className="rounded-2xl p-5" style={{ background: c.bg, border: `1px solid ${c.border}` }}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] font-bold tracking-[0.12em] uppercase mb-3" style={{ color: `${c.text}80` }}>
            {label}
          </p>
          <p className="text-[32px] font-bold leading-none" style={{ color: c.text }}>{value}</p>
        </div>
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: `${c.text}18` }}
        >
          <Icon className="w-4.5 h-4.5" style={{ color: c.text }} />
        </div>
      </div>
    </div>
  );
};

const OcorrenciaCard = ({ oc, vigilante, posto, resolvida, onAcompanhar }) => (
  <article
    data-testid={`oc-row-${oc.id}`}
    className="rounded-2xl p-4 transition-all"
    style={{
      background: resolvida ? "rgba(16,185,129,0.03)" : "rgba(255,255,255,0.03)",
      border: `1px solid ${resolvida ? "rgba(16,185,129,0.1)" : "rgba(255,255,255,0.08)"}`,
    }}
  >
    <div className="flex items-start gap-3 mb-3">
      <div
        className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{
          background: resolvida
            ? "rgba(16,185,129,0.1)"
            : oc.prioridade === "alta"
            ? "rgba(239,68,68,0.1)"
            : "rgba(245,158,11,0.1)",
        }}
      >
        {resolvida ? (
          <CheckCircle2 className="w-4 h-4" style={{ color: "#10B981" }} />
        ) : (
          <AlertTriangle
            className="w-4 h-4"
            style={{ color: oc.prioridade === "alta" ? "#EF4444" : "#F59E0B" }}
          />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold tracking-wider uppercase mb-0.5" style={{ color: "rgba(255,255,255,0.3)" }}>
              {oc.categoria}
            </p>
            <h4 className="text-[14px] font-bold text-white/90 leading-snug truncate">{oc.titulo}</h4>
          </div>
          <PriorityBadge prioridade={oc.prioridade} />
        </div>
      </div>
    </div>

    <p className="text-[13px] leading-relaxed mb-3 line-clamp-2" style={{ color: "rgba(255,255,255,0.45)" }}>
      {oc.descricao}
    </p>

    <div className="flex items-center gap-3 mb-3">
      <span className="flex items-center gap-1.5 text-[11px]" style={{ color: "rgba(255,255,255,0.35)" }}>
        <UserCircle className="w-3.5 h-3.5" />
        {vigilante?.nome || "Não informado"}
      </span>
      <span className="flex items-center gap-1.5 text-[11px]" style={{ color: "rgba(255,255,255,0.35)" }}>
        <MapPin className="w-3.5 h-3.5" />
        {posto?.nome || "—"}
      </span>
      <span className="flex items-center gap-1.5 text-[11px] ml-auto" style={{ color: "rgba(255,255,255,0.25)" }}>
        <Clock className="w-3.5 h-3.5" />
        {formatDataHora(oc.criadoEm)}
      </span>
    </div>

    <div className="flex items-center justify-between pt-3" style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
      <span className="text-[11px]" style={{ color: "rgba(255,255,255,0.25)" }}>Via app do vigilante</span>
      <button
        type="button"
        onClick={onAcompanhar}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold transition"
        style={{
          background: "rgba(255,255,255,0.05)",
          border: "1px solid rgba(255,255,255,0.1)",
          color: "rgba(255,255,255,0.7)",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.95)")}
        onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.7)")}
      >
        <Eye className="w-3.5 h-3.5" />
        Acompanhar
      </button>
    </div>
  </article>
);

const OcorrenciaModal = ({ ocorrencia, onClose, onResolver }) => {
  const resolvida = ocorrencia.status === "resolvida";

  return (
    <div className="fixed inset-0 z-[999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-6">
      <div
        className="w-full max-w-2xl rounded-2xl overflow-hidden shadow-2xl"
        style={{ background: "#08111F", border: "1px solid rgba(255,255,255,0.1)" }}
      >
        <div className="px-6 py-5 flex items-start justify-between gap-4" style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
          <div>
            <p className="text-[10px] font-bold tracking-[0.12em] uppercase mb-1" style={{ color: "rgba(255,255,255,0.3)" }}>
              Acompanhamento
            </p>
            <h2 className="text-[18px] font-bold text-white">{ocorrencia.titulo}</h2>
            <p className="text-[12px] mt-0.5" style={{ color: "rgba(255,255,255,0.4)" }}>
              {ocorrencia.categoria} · {formatDataHora(ocorrencia.criadoEm)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-xl flex items-center justify-center transition"
            style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.09)", color: "rgba(255,255,255,0.5)" }}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="flex flex-wrap items-center gap-2">
            <PriorityBadge prioridade={ocorrencia.prioridade} />
            {resolvida ? (
              <StatusBadge tone="success">resolvida</StatusBadge>
            ) : (
              <StatusBadge tone="warning">aberta</StatusBadge>
            )}
          </div>

          <div className="rounded-xl p-4" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)" }}>
            <p className="text-[10px] font-bold tracking-[0.1em] uppercase mb-2" style={{ color: "rgba(255,255,255,0.3)" }}>Descrição</p>
            <p className="text-[14px] leading-relaxed" style={{ color: "rgba(255,255,255,0.75)" }}>
              {ocorrencia.descricao || "Sem descrição informada."}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <InfoBox icon={UserCircle} label="Registrado por"  value={ocorrencia.vigilante?.nome || "Não informado"} />
            <InfoBox icon={MapPin}     label="Posto"           value={ocorrencia.posto?.nome || "Não informado"} />
            <InfoBox icon={Clock}      label="Data e hora"     value={formatDataHora(ocorrencia.criadoEm)} />
            <InfoBox icon={ShieldCheck} label="Encaminhamento" value={resolvida ? "Ocorrência encerrada" : "Aguardando acompanhamento"} />
          </div>

          {/* Linha de tratamento */}
          <div className="rounded-xl p-4 space-y-3" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}>
            <p className="text-[10px] font-bold tracking-[0.1em] uppercase mb-1" style={{ color: "rgba(255,255,255,0.3)" }}>Linha de tratamento</p>
            <TratamentoItem titulo="Ocorrência registrada" detalhe="Registro enviado pelo app do vigilante." ativo />
            <TratamentoItem titulo="Administração notificada" detalhe="Síndico/administração pode acompanhar pelo painel." ativo />
            <TratamentoItem
              titulo="Encerramento"
              detalhe={resolvida ? "Ocorrência marcada como resolvida." : "Aguardando resolução."}
              ativo={resolvida}
            />
          </div>
        </div>

        <div className="px-6 py-4 flex justify-end gap-2" style={{ borderTop: "1px solid rgba(255,255,255,0.07)" }}>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 rounded-xl text-[13px] font-semibold transition"
            style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.09)", color: "rgba(255,255,255,0.6)" }}
          >
            Fechar
          </button>
          {!resolvida && (
            <button
              type="button"
              onClick={onResolver}
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-[13px] font-bold transition"
              style={{ background: "#10B981", color: "#fff" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#059669")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "#10B981")}
            >
              <CheckCircle2 className="w-4 h-4" />
              Marcar como resolvida
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const InfoBox = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl p-3" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}>
    <div className="flex items-start gap-2.5">
      <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "#3B82F6" }} />
      <div>
        <p className="text-[10px] font-semibold mb-0.5" style={{ color: "rgba(255,255,255,0.3)" }}>{label}</p>
        <p className="text-[13px]" style={{ color: "rgba(255,255,255,0.8)" }}>{value}</p>
      </div>
    </div>
  </div>
);

const TratamentoItem = ({ titulo, detalhe, ativo }) => (
  <div className="flex items-start gap-3">
    <div
      className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
      style={{
        background: ativo ? "rgba(16,185,129,0.15)" : "rgba(255,255,255,0.05)",
      }}
    >
      {ativo ? (
        <CheckCircle2 className="w-3.5 h-3.5" style={{ color: "#10B981" }} />
      ) : (
        <div className="w-2 h-2 rounded-full" style={{ background: "rgba(255,255,255,0.2)" }} />
      )}
    </div>
    <div>
      <p className="text-[13px] font-semibold" style={{ color: ativo ? "rgba(255,255,255,0.85)" : "rgba(255,255,255,0.35)" }}>{titulo}</p>
      <p className="text-[12px] mt-0.5" style={{ color: "rgba(255,255,255,0.3)" }}>{detalhe}</p>
    </div>
  </div>
);
